import Handlebars from '@sparta.global.vendors/handlebars';
import MessagingDefaultsUtility from '@sparta.global.utilities/messaging-defaults-utility/dist/es6.js';

// Constant for storing message strings.
const STRINGS = {
  MSG_NO_JCR: 'No JCR objects provided to helper.'
};

// Shared defaults between helper and utility
const DEFAULTS = MessagingDefaultsUtility.getDefaultConfig();

/**
 * Get the HTML blocks between opening and closing of helper tags in HBS file.
 * @param {context} context HTML that is between the item brackets.
 * @param {object} options JSON object used to build the msg group.
 * @returns {void}
 */
function getHelperContext(context, options) {
  // Set block content.
  let content = '';
  if (options && options.isBlockHelperOptions) {
    const blockHelperTemplate = Handlebars.compile(`${options.helperBlockContent}`);
    content = blockHelperTemplate(context);
  }
  if (options && Object.prototype.hasOwnProperty.call(options, 'fn')) {
    content = options.fn(context);
  }
  return content;
}

/**
 * Returns message content as a list or plain string
 * @param {string || array} content Items that will get parsed into HTML.
 * @param {object} settings JSON object used to build the msg group.
 * @returns {void}
 */
function formatMessage(content, settings) {
  const options = settings.options;
  const iconName = options.defaultStrings[options.language][settings.type];
  const hiddenIcon = `<span class="ada-hidden">${iconName}</span>`;
  // Format list markup
  if (Array.isArray(content)) {
    let str = '';
    let startWrap = '';
    let endWrap = '';
    let listTypeTag = '';
    if (options.listType === 'ordered' || options.listType === 'unordered') {
      startWrap = '<li role="listitem">';
      endWrap = '</li>';
      listTypeTag = options.listType === 'ordered' ? "ol" : "ul";
      str += `<${listTypeTag} class="spartaMessage-list" role="list" ${settings.title ? '' : 'tabIndex="-1"'}>${settings.title ? '' : hiddenIcon}`;
    }
    content.forEach((item, index) => {
      if (options.listType === 'paragraph') {
        endWrap = '</p>';
        if (index === 0 && !settings.title) {
          startWrap = `<p class="spartaMessage-text" tabIndex="-1">${hiddenIcon}`;
        } else {
          startWrap = '<p class="spartaMessage-text">';
        }
      }
      str += `${startWrap}${item}${endWrap}`;
    });
    if (options.listType === 'ordered' || options.listType === 'unordered') {
      str += `</${listTypeTag}>`;
    }
    return new Handlebars.SafeString(str);
  }

  // Format paragraph content
  return content && content !== undefined && content !== '' ? new Handlebars.SafeString(`<p class="spartaMessage-text" ${settings.title ? '' : 'tabIndex="-1"'}>${settings.title ? '' : hiddenIcon}${content}</p>`) : '';
}

/**
 * Returns the BAU messageing utility original output
 * @param {object} config JSON object used to build the msg group.
 * @param {object} content Formatted HTML to be injected itno the message body.
 * @returns {void}
 */
function buildTemplate(config, content) {
  let borderWidthClass = '';
  let dismiss = '';
  let remind = '';
  let neverShow = '';
  let titleClass = '';
  let dismissClass = '';
  let dismissContainer = '';
  const options = config.options;
  const closeText = config.closeText;
  const closeMessageADAText = config.closeMessageADAText;
  const iconName = options.defaultStrings[options.language][config.type];
  if (options.border !== null) {
    if (options.border == '0' || options.border == '0px') {
      borderWidthClass = 'spartaMessage-border--none';
    }
  }
  if (options.dismiss && options.size !== 'xsmall') {
    dismissClass = 'dismiss';
    if (options.dismissADALabel === undefined) {
      options.dismissADALabel = '';
    }
    dismiss = `
      <button type="button" class="spartaMessage-close-x spa-icon">
        <span class="ada-hidden spartaMessage-close-msg">
          ${closeText} ${iconName} ${closeMessageADAText}
        </span>
      </button>
    `.trim();
    dismissContainer = `<div class="spartaMessage-dismiss">${dismiss}</div>`;
  }
  if (options.remind === false) {
    remind = '<span class="spartaMessage-remind-me-later">Remind Me Later</span>';
  }
  if (options.neverShow === false) {
    neverShow = '<span class="spartaMessage-never-show">Never Show Again</span>';
  }

  // if a title value is included format it
  const title = config.title !== '' && config.title !== undefined ? `<p class="spartaMessage-title" tabIndex="-1"><span class="ada-hidden">${iconName}</span>${config.title}</p>` : '';
  if (!config.title || config.title === undefined) {
    titleClass = 'no-title';
  }
  const messageDataOptions = config.renderDataOptions ? `data-options="${Handlebars.Utils.escapeExpression(JSON.stringify(config))}"` : '';
  const messageClasses = `spartaMessage ${config.type} ${options.size} ${borderWidthClass}`.trim();
  const output = `
    <div id="sparta-message-${config.uuid}" class="${messageClasses}" tabIndex="-1" ${messageDataOptions}>
      <div class="spartaMessage-icon" aria-hidden="true">
        <span class="data-icon"></span>
      </div>
      <div class="spartaMessage-content ${titleClass} ${dismissClass}">
        ${title}
        ${content}
      </div>
      ${dismissContainer}
      <div class="spartaMessage-remind">
        ${remind}
      </div>
      <div class="spartaMessage-neverShow">
        ${neverShow}
      </div>
    </div>
  `.trim();
  return new Handlebars.SafeString(output);
}

/**
 * @method messagingHelper
 * @param {object} config JSON object used to build the msg group.
 * @param {object} context HTML that is between the item brackets.
 * @returns {Node} `Handlebars.SafeString`.
 */
function messagingHelper(config, context) {
  if (!config) {
    throw new Error(`${STRINGS.MSG_NO_JCR}:msgHelper`);
  }

  // Does helper get generated from HBS or JS file
  // Note: The HBS rendered helper contains global data root while JS does not.
  // This allows us to render the JCR options into the DOM which HSB helper may need for utility events.
  const contextTesting = context ? 'global' in context.data.root : undefined;

  // Does incoming data define printing of config JCR as data attribute
  const renderDataOptions = 'renderDataOptions' in config ? config.renderDataOptions : contextTesting;

  // Get content from helper block if present
  const helperContent = getHelperContext(config, context);
  const settings = {
    uuid: config.uuid || ((1 + Math.random()) * 0x10000 || 0).toString(16).substring(1),
    type: config.type,
    title: config.title,
    content: config.content,
    renderDataOptions: renderDataOptions || contextTesting ? renderDataOptions : false,
    options: Object.assign({}, DEFAULTS, config.options)
  };

  // Update config based on JCR ADA values
  if (config.options && config.options.closeText) {
    settings.closeText = config.options.closeText;
  } else {
    settings.closeText = settings.options.defaultStrings[settings.options.language].closeText;
  }
  if (config.options && config.options.closeMessageADAText) {
    settings.closeMessageADAText = config.options.closeMessageADAText;
  } else {
    settings.closeMessageADAText = settings.options.defaultStrings[settings.options.language].closeMessageADAText;
  }

  // Create content.
  // Checks if content comes from block helper or JCR before formatting it.
  let content = helperContent !== '' ? helperContent : settings.content;
  content = formatMessage(content, settings);
  return new Handlebars.SafeString(buildTemplate(settings, content));
}

/**
 * Format a string or array into HTML structure
 * @param {*} content String or array of content to format
 * @param {*} settings Instance object of different parameters
 * @returns {void}
 */
function messagingFormatHelper(content, settings) {
  return formatMessage(content, settings);
}

/**
 * @class messagingHelper
 * @description Class used to register helper with Handlebars.
 */

export { messagingFormatHelper, messagingHelper };
